
import { Vue, Component } from 'vue-property-decorator';
import {
  addIntentionalDelay, fetchUserInfo, getCountriesData,
} from '@/features/common/services/common.services';
import { getUserPreferences } from '@/features/settings/services/preferences.services';
import {
  checkToShowSummaryPage,
  createLabelForCarriers,
  fetchSupportedServiceLevels,
  getInProgressData,
  getIntegrationsConnectedData,
  getPaymentInformation,
  getShipmentPrintData,
  getShipmentPrintLabelSrc,
  parseCarriersData,
  syncInProgressData,
} from '@/features/shipment/services/shipment.services';
import { getUserNotificationsPrefs } from '@/features/settings/services/notifications.services';
import { getAddressesBook } from '@/features/shipment/services/addresses-book.services';
import { shipmentAdvisorNotificationStatus } from '@/features/shipment/services/advisor.services';
import { getShipment } from '@/features/shipment-history/services/shipment-history.services';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getToken } from '@/features/auth/helpers';
import {
  createLabelPayload,
  inProgressPayload,
  inProgressPayloadAfterServiceSelected,
  ratesPayload,
  ratesPayloadForReturn,
  secureshipTrackingNo,
} from '../constants/shipment';

@Component
export default class Simulation extends Vue {
  private delay = 2;

  private addDelay = () => {
    addIntentionalDelay(this.delay);
  }

  private loadApp = async () => {
    fetchUserInfo();
    getCountriesData();
    getUserPreferences();
    getInProgressData();
    getIntegrationsConnectedData();
    getUserNotificationsPrefs();
    getAddressesBook();
  }

  private createShipmentSteps: string[] = [];

  private isLoadingCreateShipment = false;

  private isRedirectToDashboard = false;

  private async createShipment() {
    this.isLoadingCreateShipment = true;

    this.createShipmentSteps = ['Loading initial data'];
    await this.loadApp();
    this.createShipmentSteps.push('Loaded initial data');

    await addIntentionalDelay(this.delay);

    this.createShipmentSteps.push('Starting create shipment');

    this.createShipmentSteps.push('Saving in progress data');
    await syncInProgressData(inProgressPayload as any);
    this.createShipmentSteps.push('Saved in progress data');

    this.createShipmentSteps.push('Checking shipment advisor notification status');
    await shipmentAdvisorNotificationStatus();
    this.createShipmentSteps.push('Checked shipment advisor notification status');

    this.createShipmentSteps.push('Getting rates');
    fetchSupportedServiceLevels();
    checkToShowSummaryPage();
    await parseCarriersData(ratesPayload as any, 'CreateShipment');
    this.createShipmentSteps.push('Got rates');

    await addIntentionalDelay(this.delay);

    this.createShipmentSteps.push('Selected a carrier');
    this.createShipmentSteps.push('Saving in progress data');
    await syncInProgressData(inProgressPayloadAfterServiceSelected as any);
    this.createShipmentSteps.push('Saved in progress data');

    this.createShipmentSteps.push('Getting summary details');
    await getPaymentInformation();
    parseCarriersData(ratesPayload as any, 'CreateShipment');
    this.createShipmentSteps.push('Got summary details');

    await addIntentionalDelay(this.delay);

    this.createShipmentSteps.push('Creating label');
    await createLabelForCarriers(createLabelPayload as any);
    this.createShipmentSteps.push('Label created');

    this.createShipmentSteps.push('Fetching print page & return shipments data');
    getShipmentPrintData(secureshipTrackingNo);
    getShipment(secureshipTrackingNo);
    parseCarriersData(ratesPayloadForReturn as any, 'CreateShipment');
    getShipmentPrintLabelSrc(secureshipTrackingNo, 'Thermal');

    if (this.isRedirectToDashboard) {
      this.createShipmentSteps.push('Redirecting to dashboard');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      this.$router.push('/dashboard');
    }

    this.createShipmentSteps.push('Done!');
    this.isLoadingCreateShipment = false;
  }

  private preferencesStressTestCount = 50;

  private runPreferencesStressTest() {
    for (let i = 0; i < this.preferencesStressTestCount; i += 1) {
      getUserPreferences();
    }
  }

  private cancelWhenInMilliseconds = 1000;

  private cancelUsingAbort() {
    const initialBaseUrl = process.env.VUE_APP_API_BASE_URL;
    const axiosBaseUrl = `${initialBaseUrl}/v2`;

    const axiosService = axios.create({
      baseURL: axiosBaseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    axiosService.interceptors.request.use((config: AxiosRequestConfig): AxiosRequestConfig => {
      const configCopy = { ...config };
      const token = getToken();
      if (token) {
        configCopy.headers.Authorization = `Bearer ${encodeURIComponent(token.replace(/"/g, '').trim())}`;
      }
      return configCopy;
    }, (error: AxiosError) => {
      Promise.reject(error);
    });

    const source = axios.CancelToken.source();

    try {
      axiosService.post(
        '/carriers/rates?caller=CreateShipment',
        ratesPayload,
        {
          cancelToken: source.token,
        },
      );
    } catch (err) {
      return;
    }

    setTimeout(() => {
      source.cancel('Request canceled by the user.');
    }, this.cancelWhenInMilliseconds);
  }

  private microserviceDelayInSeconds = 3;

  private axiosCancelTokenSource = axios.CancelToken.source();

  private async testMicroserviceDelay() {
    const initialBaseUrl = process.env.VUE_APP_API_BASE_URL;
    const axiosBaseUrl = `${initialBaseUrl}/v1`;

    const axiosService = axios.create({
      baseURL: axiosBaseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    axiosService.interceptors.request.use((config: AxiosRequestConfig): AxiosRequestConfig => {
      const configCopy = { ...config };
      const token = getToken();
      if (token) {
        configCopy.headers.Authorization = `Bearer ${encodeURIComponent(token.replace(/"/g, '').trim())}`;
      }
      return configCopy;
    }, (error: AxiosError) => {
      Promise.reject(error);
    });

    this.axiosCancelTokenSource = axios.CancelToken.source();

    try {
      if (!this.microserviceDelayInSeconds) {
        axiosService.get(
          '/test-data/microservice-delay',
          {
            cancelToken: this.axiosCancelTokenSource.token,
          },
        );
        return;
      }

      axiosService.get(
        `/test-data/microservice-delay/${this.microserviceDelayInSeconds}`,
        {
          cancelToken: this.axiosCancelTokenSource.token,
        },
      );
    } catch (err) {
      console.log('error', err);
    }
  }

  private cancelTimeInSecondsForMicroservice = 1000;

  private cancelMicroserviceRequestImmediately() {
    setTimeout(() => {
      this.axiosCancelTokenSource.cancel('Request canceled by the user.');
    }, this.cancelTimeInSecondsForMicroservice);
  }
}
